import React from 'react';
import { fireEvent, render, screen } from '@testing-library/react';
import Alert from './Alert';

jest.mock('components/styleguide/icon/Icon', () => ({
  __esModule: true,
  default: ({ name, ...props }: any) => <span data-icon={name} {...props} />,
}));

describe('Alert', () => {
  it('renders message text', () => {
    render(<Alert message="Something happened" />);
    expect(screen.getByText('Something happened')).toBeInTheDocument();
  });

  it('renders JSX message content', () => {
    render(
      <Alert message={<span>Check our <a href="/terms">terms</a></span>} />
    );
    expect(screen.getByText('terms')).toBeInTheDocument();
    expect(screen.getByRole('link', { name: 'terms' })).toHaveAttribute('href', '/terms');
  });

  it('renders message with nested HTML elements', () => {
    render(
      <Alert message={<><strong>Important:</strong> Please update your profile</>} />
    );
    expect(screen.getByText('Important:')).toBeInTheDocument();
    expect(screen.getByText('Please update your profile')).toBeInTheDocument();
  });

  it('renders HTML string message with dangerouslySetInnerHTML', () => {
    render(<Alert message="Please accept our <a href='/terms'>Terms</a>" />);
    const link = screen.getByRole('link', { name: 'Terms' });
    expect(link).toHaveAttribute('href', '/terms');
  });

  it('defaults to info type', () => {
    const { container } = render(<Alert message="Info" />);
    expect(container.querySelector('.info')).toBeInTheDocument();
  });

  describe('type variants', () => {
    const types = ['info', 'success', 'warning', 'error', 'ai', 'globe'] as const;

    types.forEach(type => {
      it(`renders ${type} variant`, () => {
        const { container } = render(<Alert message="Test" type={type} />);
        expect(container.querySelector(`.${type}`)).toBeInTheDocument();
      });
    });
  });

  describe('icons', () => {
    it('renders info icon for info type', () => {
      const { container } = render(<Alert message="Info" type="info" />);
      expect(container.querySelector('[data-icon="info"]')).toBeInTheDocument();
    });

    it('renders circleCheck icon for success type', () => {
      const { container } = render(<Alert message="Success" type="success" />);
      expect(container.querySelector('[data-icon="circleCheck"]')).toBeInTheDocument();
    });

    it('renders circleAlert icon for warning type', () => {
      const { container } = render(<Alert message="Warning" type="warning" />);
      expect(container.querySelector('[data-icon="circleAlert"]')).toBeInTheDocument();
    });

    it('renders triangleAlert icon for error type', () => {
      const { container } = render(<Alert message="Error" type="error" />);
      expect(container.querySelector('[data-icon="triangleAlert"]')).toBeInTheDocument();
    });

    it('marks icon as aria-hidden', () => {
      const { container } = render(<Alert message="Test" />);
      const icon = container.querySelector('[data-icon="info"]');
      expect(icon).toHaveAttribute('aria-hidden', 'true');
    });
  });

  describe('accessibility', () => {
    it('has role="status" for info type', () => {
      render(<Alert message="Info" type="info" />);
      expect(screen.getByRole('status')).toBeInTheDocument();
    });

    it('has role="status" for success type', () => {
      render(<Alert message="Done" type="success" />);
      expect(screen.getByRole('status')).toBeInTheDocument();
    });

    it('has role="alert" for warning type', () => {
      render(<Alert message="Watch out" type="warning" />);
      expect(screen.getByRole('alert')).toBeInTheDocument();
    });

    it('has role="alert" for error type', () => {
      render(<Alert message="Failed" type="error" />);
      expect(screen.getByRole('alert')).toBeInTheDocument();
    });

    it('has role="status" for ai type', () => {
      render(<Alert message="AI" type="ai" />);
      expect(screen.getByRole('status')).toBeInTheDocument();
    });

    it('has role="status" for globe type', () => {
      render(<Alert message="Globe" type="globe" />);
      expect(screen.getByRole('status')).toBeInTheDocument();
    });
  });

  describe('close button', () => {
    it('does not render close button by default', () => {
      render(<Alert message="Test" />);
      expect(screen.queryByLabelText('Close alert')).not.toBeInTheDocument();
    });

    it('renders close button when showCloseButton is true', () => {
      render(<Alert message="Test" showCloseButton />);
      expect(screen.getByLabelText('Close alert')).toBeInTheDocument();
    });

    it('hides alert on close click', () => {
      const { container } = render(<Alert message="Test" showCloseButton />);
      fireEvent.click(screen.getByLabelText('Close alert'));
      expect(container.querySelector('.alert')).not.toBeInTheDocument();
    });

    it('calls onClose callback', () => {
      const onClose = jest.fn();
      render(<Alert message="Test" showCloseButton onClose={onClose} />);
      fireEvent.click(screen.getByLabelText('Close alert'));
      expect(onClose).toHaveBeenCalledTimes(1);
    });

    it('has type="button" on close button', () => {
      render(<Alert message="Test" showCloseButton />);
      expect(screen.getByLabelText('Close alert')).toHaveAttribute('type', 'button');
    });

    it('marks close icon as aria-hidden', () => {
      render(<Alert message="Test" showCloseButton />);
      const closeBtn = screen.getByLabelText('Close alert');
      const icon = closeBtn.querySelector('[data-icon="close"]');
      expect(icon).toHaveAttribute('aria-hidden', 'true');
    });
  });

  it('applies custom className', () => {
    const { container } = render(<Alert message="Test" className="custom" />);
    expect(container.firstChild).toHaveClass('custom');
  });
});
